import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useAuth, type AdminUser } from '@/context/AuthContext'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

type Status =
  | { kind: 'verifying' }
  | { kind: 'success' }
  | { kind: 'error', message: string }

export default function VerifyMagicLink() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { login } = useAuth()
  const [status, setStatus] = useState<Status>({ kind: 'verifying' })
  const ranRef = useRef(false)  // guard against StrictMode double-invoke

  useEffect(() => {
    if (ranRef.current) return
    ranRef.current = true

    const token = params.get('token')
    if (!token) {
      setStatus({ kind: 'error', message: 'Falta el token en el enlace.' })
      return
    }

    api<{ ok: boolean; session_token: string; user: AdminUser }>('/auth/verify.php', {
      method: 'POST',
      body: { token },
    })
      .then(res => {
        login(res.session_token, res.user)
        setStatus({ kind: 'success' })
        setTimeout(() => navigate('/', { replace: true }), 900)
      })
      .catch(err => {
        const msg = err instanceof ApiError ? err.message : 'Error de conexión'
        setStatus({ kind: 'error', message: msg })
      })
  }, [params, login, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center">
          {status.kind === 'verifying' && (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-slate-100 mb-4">
                <svg className="w-7 h-7 text-slate-400 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold mb-2">Verificando enlace…</h2>
              <p className="text-sm text-slate-500">Un momento, te llevamos al panel.</p>
            </>
          )}

          {status.kind === 'success' && (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-4">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold mb-2">¡Listo!</h2>
              <p className="text-sm text-slate-500">Entrando al panel…</p>
            </>
          )}

          {status.kind === 'error' && (
            <>
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-50 text-red-600 mb-4">
                <svg className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold mb-2">No pudimos verificar el enlace</h2>
              <p className="text-sm text-slate-500 mb-6">{status.message}</p>
              <Button asChild className="w-full">
                <Link to="/login">Solicitar uno nuevo</Link>
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
